import React, { memo } from 'react';
import type { ManagedFormFieldProps } from '../../lib';

/**
 * Props of a {@link FastFormField}
 */
// eslint-disable-next-line @typescript-eslint/no-restricted-types
export type FastFormFieldProps<T extends object, Name extends keyof T> = {
  /**
   * Render method to render the children of the field.
   * @param field the managedForm field properties
   */
  render: (field: ManagedFormFieldProps<T, Name>) => JSX.Element;
  /**
   * The field retrieved when using the `getField` on a managedForm.
   */
  field: ManagedFormFieldProps<T, Name>;
};

/**
 * The inner component.
 *
 * @param parameters {@link FastFormFieldProps}
 * @return the created {@link JSX.Element}.
 */
// eslint-disable-next-line @typescript-eslint/no-restricted-types
function FastFormFieldCmp<T extends object, Name extends keyof T>({
  render,
  field
}: Readonly<FastFormFieldProps<T, Name>>): JSX.Element {
  return render(field);
}

/**
 * Function to check if the memoized component should render or not.
 * It will render if this function returns true.
 *
 * @param prevProps properties of previous render
 * @param nextProps properties of next render
 * @return `true` if no render is needed, else `false`.
 * It will be false if any of
 * <ul>
 *     <li>value</li>
 *     <li>touched</li>
 *     <li>name</li>
 *     <li>error</li>
 * </ul>
 * changed
 */
function areEqual(prevProps: unknown, nextProps: unknown) {
  // eslint-disable-next-line @typescript-eslint/no-restricted-types
  const field = (prevProps as FastFormFieldProps<object, never>).field;
  // eslint-disable-next-line @typescript-eslint/no-restricted-types
  const nextField = (nextProps as FastFormFieldProps<object, never>).field;
  return (
    field.value === nextField.value &&
    field.touched === nextField.touched &&
    field.name === nextField.name &&
    field.error === nextField.error
    // onChange and onBlur are always different object references
    // so those 2 simply can't be part of this check for this to work
    // (but they shouldn't change anyway, so it doesn't matter)
  );
}

/**
 * A component to wrap children that use {@link useManagedForm} field properties for their rendering.
 *
 * This component uses <a href="https://reactjs.org/docs/react-api.html#reactmemo">`memo`</a> with an equality check function
 * to prevent unnecessary rendering when properties in the managedForm change which this component doesn't care about change.
 *
 * This component will only render when any of
 * <ul>
 *     <li>value</li>
 *     <li>touched</li>
 *     <li>name</li>
 *     <li>error</li>
 * </ul>
 * of its `managedForm` field props changes.
 *
 * This component has 2 generic typings, but Typescript (and IntelliJ) should be smart enough to infer both of them
 * when using a proper managedForm field as its `field` property.
 *
 */
export const FastFormField = (memo(
  FastFormFieldCmp,
  areEqual
) as unknown) as typeof FastFormFieldCmp;
